import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router';
import api from '../src/utils/axiosConfig';
import { Check, AlertCircle, ArrowLeft } from 'lucide-react';

const AcceptOrganizationInvitation = () => {
  const location = useLocation();

  const queryParams = new URLSearchParams(location.search);
  const token = queryParams.get('token');

  const navigate = useNavigate();

  const [invitationData, setInvitationData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (token) {
      const fetchData = async () => {
        await fetchInvitationData(token);
      };
      fetchData();
    }
  }, [token]);

  const fetchInvitationData = async (invitationToken) => {
    setLoading(true);
    setError(null);

    try {
      const response = await api.get(`/invitations/${invitationToken}/`);
      setInvitationData(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching invitation:', error);
      setError(
        error.response?.status !== 200
          ? 'This invitation link is invalid or has expired.'
          : 'Failed to load invitation. Please try again.'
      );
      setLoading(false);
    }
  };

  const handleAcceptInvitation = async () => {
    setSubmitting(true);
    setError(null);

    try {
      await api.post(`/invitations/${token}/accept/`);

      setSuccess(true);

      // After 2 seconds, redirect to dashboard
      setTimeout(() => {
        navigate('/');
      }, 2000);

    } catch (error) {
      console.error('Error accepting invitation:', error);
      setError('Failed to accept invitation. Please try again.');
      setSubmitting(false);
    }
  };


  const roleLabels = {
    'admin': 'Admin',
    'event_manager': 'Event Manager',
    'usher': 'Usher',
    'submissions_manager': 'Submissions Manager',
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="flex flex-col items-center">
          <svg className="animate-spin h-12 w-12 text-indigo-600 mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="text-gray-600">Verifying invitation...</p>
        </div>
      </div>
    );
  }

  if (error && !invitationData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100 p-4">
        <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-8 text-center">
          <AlertCircle size={48} className="mx-auto text-red-500 mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Invitation Error</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <a href="/dashboard" className="inline-block px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors">
            Return to Dashboard
          </a>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100 p-4">
        <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-8 text-center">
          <div className="w-16 h-16 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-4">
            <Check size={32} className="text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Invitation Accepted!</h2>
          <p className="text-gray-600 mb-6">
            You have successfully joined <span className="font-semibold">{invitationData.organization.name}</span>.
            Redirecting to dashboard...
          </p>
          <div className="w-12 h-1 bg-gray-200 mx-auto rounded-full overflow-hidden">
            <div className="bg-indigo-600 h-1 animate-pulse"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 p-4">
      <div className="max-w-md w-full bg-white shadow-lg rounded-lg overflow-hidden">
        <div className="bg-indigo-600 px-6 py-4">
          <h2 className="text-xl font-bold text-white">Organization Invitation</h2>
        </div>

        {invitationData && (
          <div className="p-6">
            <div className="mb-6">
              <div className="flex items-center justify-center mb-6">
                {invitationData.organization.logo ? (
                  <img
                    src={invitationData.organization.logo}
                    alt={`${invitationData.organization.name} logo`}
                    className="h-20 w-20 object-contain"
                  />
                ) : (
                  <div className="h-20 w-20 bg-indigo-100 rounded-full flex items-center justify-center text-indigo-600 font-bold text-xl">
                    {invitationData.organization.name.substring(0, 2).toUpperCase()}
                  </div>
                )}
              </div>

              <h3 className="text-xl font-semibold text-center mb-2">
                {invitationData.organization.name}
              </h3>

              <p className="text-gray-700 text-center mb-4">
                You've been invited to join as a <span className="font-semibold">{roleLabels[invitationData.role]}</span>
              </p>

              {invitationData.message && (
                <div className="bg-gray-50 p-4 rounded-md mb-4">
                  <p className="text-sm text-gray-700">{invitationData.message}</p>
                </div>
              )}
            </div>

            {error && (
              <div className="mb-6 p-4 bg-red-50 text-red-700 rounded-md flex items-start">
                <AlertCircle size={20} className="mr-2 flex-shrink-0 mt-0.5" />
                <p>{error}</p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4 mt-8">


              <button
                onClick={handleAcceptInvitation}
                disabled={submitting}
                className="flex justify-center items-center px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 transition-colors"
              >
                {submitting ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Processing...
                  </>
                ) : (
                  <>
                    <Check size={20} className="mr-2" />
                    Accept Invitation
                  </>
                )}
              </button>
            </div>

            <div className="mt-6 border-t border-gray-200 pt-4">
              <a
                href="/dashboard"
                className="inline-flex items-center text-sm text-indigo-600 hover:text-indigo-500 transition-colors"
              >
                <ArrowLeft size={16} className="mr-1" />
                Back to Dashboard
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AcceptOrganizationInvitation;